/**
 * DOCTOR-WORKSPACE-COMPLETION-V1 -- the DOCTOR LENS
 * (`/dashboard/doctor/patient/:patientId`): a doctor-shaped Patient 360.
 *
 * Founder ruling 2026-07-17 (OPTION B): a doctor-SPECIFIC lens, NOT the
 * receptionist Patient Master. It reuses the EXISTING Patient 360 render
 * grammar (patient-360-view.ts label maps + honest vocabulary + the ONE money
 * formatter) and the EXISTING consult door -- never a second Patient 360
 * component family, never a second money renderer.
 *
 * IT SHOWS (lawful care context): identity + care demographics; the clinical +
 * intake case history; today / recent / upcoming visits; the multi-visit
 * treatment journey (completed vs remaining + the lawful course remainder);
 * follow-up context; the next appointment; lawful commercial evidence
 * (recorded payments, void history, accepted treatment amounts) with honest
 * unavailable states; and the CONTINUE-CARE door (the existing consult
 * workspace where the existing check-in gate permits; an honest reason where
 * it does not).
 *
 * IT NEVER: computes an account balance (the forbidden vocabulary is absent);
 * shows raw contact (contact stays masked; reveal is not a doctor action);
 * grants receptionist / billing-entry / identity-merge / discount-approval /
 * contact-reveal / administrative Patient Master actions.
 */

import { useEffect, useState } from 'react';
import { clinicLabelOf } from '@/dashboard/clinic-context';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  fetchDoctorPatientLens,
  type DoctorPatientLens,
} from '@/dashboard/api';
import {
  PageHeader,
  RecordHeader,
  SectionShell,
  FieldGrid,
  AcBadge,
  KpiTile,
  EmptyState,
  LoadingState,
  type FieldGridField,
} from '@/dashboard/ui';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { formatIstDateTime } from '@/lib/datetime-ist';
import { formatWholeInr } from '@/dashboard/clinical-workbench/case-sheet-cockpit-view';
import {
  inrWordToRs,
  rupeeGlyphToRs,
} from '@/dashboard/doctor/doctor-consult-view';
import { evidenceClassLabel } from '@/dashboard/doctor/clinical-evidence-view';
import { genderAgeLabel } from '@/dashboard/patients/patient-master-view';
import {
  appointmentStatusLabel,
  appointmentDoctorQueueLabel,
  intakeEvidenceLabel,
  conversionStateLabel,
  formatPaymentAmount,
  paymentMethodLabel,
  JOURNEY_LEDGER_STATE_LABELS,
  JOURNEY_UNAVAILABLE_COPY,
  JOURNEY_COMPLETION_UNAVAILABLE_COPY,
  JOURNEY_NO_COURSES_COPY,
  PAYMENT_STATE_LABELS,
  PAYMENT_BALANCE_COPY,
  PAYMENT_HISTORY_NONE_COPY,
  PAYMENT_HISTORY_UNAVAILABLE_COPY,
  PAYMENT_VOIDED_STATUS_LABEL,
  TREATMENT_NO_ITEMS_COPY,
  TREATMENT_SUMMARY_UNAVAILABLE_COPY,
  EXTERNAL_RECORD_COPY,
  EXTERNAL_PROVENANCE_COPY,
  type Patient360AppointmentWire,
} from '@/dashboard/patients/patient-360-view';
import {
  continueCareView,
  historyIntakeAbsentCopy,
  DOCTOR_CONTACT_MASKED_COPY,
  DOCTOR_PATIENTS_HREF,
  lensBackDoor,
  parseLensOrigin,
  type LensOrigin,
} from '@/dashboard/doctor/doctor-patient-lens-view';
import { JourneyStageLadder } from '@/dashboard/ui';
import { deriveCountsStageLadder } from '@/dashboard/doctor/consult-stage-view';
import {
  consentTypeLabel,
  NEXT_VISIT_NONE_COPY,
  nextVisitIntentLine,
} from '@/dashboard/patients/patient-360-view';

/** DOCTOR-HOME-STATE-MODEL-V1 (F6a): both lens date renders now read THE ONE
 *  IST owner (lib/datetime-ist) -- the previous local `new Date(iso)` parse
 *  was host-timezone-dependent for a zone-less value (the LES-126
 *  second-formatter class). Same "13 Aug 2026" grammar; honest echo of the
 *  raw value when unparseable. */
function fmtIstDate(iso: string): string {
  const full = formatIstDateTime(iso);
  return full === '' ? iso : full.split(',')[0]!;
}

function fmtIstDateTime(iso: string): string {
  const full = formatIstDateTime(iso);
  return full === '' ? iso : full;
}

// --- The NEXT VISIT band (PATIENT-360-V1, founder MATCH 2026-08-16) --------
//
// The EXISTING continue-care band EXTENDED in place into the ONE page's
// next-visit band (D-360e: booking -> D1 intent -> honest none; the D2
// attribution names ride here read-only). The consult door, the check-in
// gate, and every existing test id are byte-preserved; the old separate
// "Follow-up context" section FOLDED into this band (one fact, one home).

function ContinueCareBand({ lens }: { lens: DoctorPatientLens }): JSX.Element {
  const view = continueCareView(lens.continueCare);
  const next = lens.nextAppointment;
  const intent = lens.nextVisitIntent ?? null;
  const attribution = lens.attribution ?? null;
  const ladder = deriveCountsStageLadder(
    lens.treatmentJourney.availability === 'available'
      ? (lens.treatmentJourney.courses[0]?.acceptedItemCount ?? null)
      : null,
    lens.treatmentJourney.availability === 'available'
      ? (lens.treatmentJourney.courses[0]?.completedItemCount ?? null)
      : null,
  );
  return (
    <SectionShell
      eyebrow="Next visit"
      title="Next visit and clinical action"
      testId="doctor-lens-continue-care"
      actions={
        (view.kind === 'consult' || view.kind === 'view') &&
        view.href !== null ? (
          <Link
            to={view.href}
            className="ac-btn ac-btn--primary ac-btn--sm"
            data-testid="doctor-lens-continue-care-door"
          >
            {view.label}
          </Link>
        ) : undefined
      }
    >
      {view.kind === 'blocked' ? (
        <p className="text-sm text-muted-foreground" data-testid="doctor-lens-continue-care-blocked">
          <AcBadge tone="pending">Consult not open yet</AcBadge>{' '}
          {view.reason}
        </p>
      ) : view.kind === 'none' ? (
        <p className="text-sm text-muted-foreground" data-testid="doctor-lens-continue-care-none">
          No appointment in progress today. Continue this patient&apos;s care
          through the treatment journey and history below.
        </p>
      ) : null}
      <FieldGrid
        testId="doctor-lens-next-appointment"
        label="Scheduling"
        fields={[
          {
            label: 'Booked',
            value:
              next !== null ? (
                <span data-testid="doctor-lens-next-appt-value">
                  {fmtIstDateTime(next.scheduledAt)}
                  {next.clinicLabel !== null ? ` - ${next.clinicLabel}` : ''}
                  {next.doctorName !== null ? ` - with ${next.doctorName}` : ''}
                </span>
              ) : intent !== null ? (
                // D-360e: no booking, a structured D1 intent exists -- the
                // honest "to schedule" state (never "Next visit set").
                <span data-testid="doctor-lens-next-visit-intent">
                  {nextVisitIntentLine(intent)}
                </span>
              ) : (
                <span data-testid="doctor-lens-next-visit-none">
                  {NEXT_VISIT_NONE_COPY}
                </span>
              ),
          },
          ...(ladder !== null && !ladder.complete
            ? [
                {
                  label: 'Stage next',
                  value: (
                    <span data-testid="doctor-lens-stage-next">
                      {`Stage ${Math.min(ladder.doneCount + 1, ladder.stageTotal)} of ${ladder.stageTotal}`}
                    </span>
                  ),
                },
              ]
            : []),
          ...(attribution !== null
            ? [
                {
                  label: 'Attribution',
                  value: (
                    <span data-testid="doctor-lens-attribution">
                      {attribution.conversionDoctorName !== null
                        ? `Converted by: ${attribution.conversionDoctorName} (set at the acceptance act - locked)`
                        : 'Converted by: not recorded'}
                      {' - '}
                      {attribution.treatmentDoctorName !== null
                        ? `Treatment doctor: ${attribution.treatmentDoctorName}`
                        : 'Treatment doctor: not recorded'}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
      />
    </SectionShell>
  );
}

// --- Consent status (PATIENT-360-V1, founder ruling D-360c: YES) -----------
//
// Status tokens from THE consent_records owner, plain labels, honest empty.
// No reveal, no free text, no channel/actor -- the wire projection is the
// fence (type + granted-at only, negative-pinned).

function ConsentStatusSection({ lens }: { lens: DoctorPatientLens }): JSX.Element {
  const consents = lens.consents ?? [];
  const unavailable = lens.consentAvailability === 'unavailable' ||
    (lens.consentAvailability !== 'available' && consents.length === 0);
  return (
    <SectionShell
      eyebrow="Consent"
      title="Privacy and intake consent"
      sub="View treatment consent in the visit."
      testId="doctor-lens-consents"
    >
      {unavailable ? (
        <p className="text-sm text-muted-foreground" data-testid="doctor-lens-consents-unavailable">
          Consent status unavailable.
        </p>
      ) : consents.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="doctor-lens-consents-empty">
          No privacy or intake consent records on file.
        </p>
      ) : (
        <p className="text-sm" data-testid="doctor-lens-consents-chips">
          {consents.map((c, i) => (
            <span key={`${c.consentType}:${i}`} className="mr-2">
              <AcBadge tone="positive">
                {consentTypeLabel(c.consentType)} - on file ({fmtIstDate(c.grantedAt)})
              </AcBadge>
            </span>
          ))}
        </p>
      )}
    </SectionShell>
  );
}

// --- Appointment lane table (today / recent / upcoming) --------------------

function AppointmentLane({
  title,
  eyebrow,
  rows,
  emptyBody,
  testId,
}: {
  title: string;
  eyebrow: string;
  rows: readonly Patient360AppointmentWire[];
  emptyBody: string;
  testId: string;
}): JSX.Element {
  return (
    <SectionShell eyebrow={eyebrow} title={title} testId={testId}>
      {rows.length === 0 ? (
        <EmptyState testId={`${testId}-empty`} title={emptyBody} />
      ) : (
        <table className="table" data-testid={`${testId}-table`}>
          <thead>
            <tr>
              <th>When</th>
              <th>Status</th>
              <th>Doctor / queue</th>
              <th>Intake</th>
              <th>Clinic</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.appointmentId} data-testid={`${testId}-row`}>
                <td>{fmtIstDateTime(a.scheduledAt)}</td>
                <td>
                  <AcBadge tone="neutral">{appointmentStatusLabel(a.status)}</AcBadge>
                </td>
                <td>{appointmentDoctorQueueLabel(a)}</td>
                <td>{intakeEvidenceLabel(a.intake)}</td>
                <td>{clinicLabelOf(a.clinicLabel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SectionShell>
  );
}

// --- Treatment journey (multi-visit courses + lawful course remainder) -----

function TreatmentJourneySection({ lens }: { lens: DoctorPatientLens }): JSX.Element {
  const journey = lens.treatmentJourney;
  return (
    <SectionShell
      eyebrow="Treatment"
      title="Multi-visit treatment journey"
      sub="Completed vs remaining accepted items, and the lawful course remainder (a plan-level obligation, never an account balance)."
      testId="doctor-lens-journey"
    >
      {journey.availability === 'unavailable' ? (
        <p className="text-sm text-muted-foreground" data-testid="doctor-lens-journey-unavailable">
          {JOURNEY_UNAVAILABLE_COPY}
        </p>
      ) : journey.courses.length === 0 ? (
        <EmptyState testId="doctor-lens-journey-empty" title={JOURNEY_NO_COURSES_COPY} />
      ) : (
        <>
        {/* D-360d: the counts ladder over the SAME journey lane (THE ONE
            stage-ladder owner; honest null = no line renders). */}
        <JourneyStageLadder
          ladder={deriveCountsStageLadder(
            journey.courses[0]?.acceptedItemCount ?? null,
            journey.courses[0]?.completedItemCount ?? null,
          )}
          courseLabel={
            journey.courses[0] !== undefined
              ? `Course #${journey.courses[0].courseId} - ${journey.courses[0].visitCount} visit${journey.courses[0].visitCount === 1 ? '' : 's'}`
              : undefined
          }
          testId="doctor-lens-stage-ladder"
        />
        <table className="table" data-testid="doctor-lens-journey-table">
          <thead>
            <tr>
              <th>Course</th>
              <th>Visits</th>
              <th>Accepted items</th>
              <th>Completed</th>
              <th>Remaining</th>
              <th>Collected</th>
              <th>Course remainder</th>
            </tr>
          </thead>
          <tbody>
            {journey.courses.map((c) => (
              <tr key={c.courseId} data-testid="doctor-lens-journey-row">
                <td>#{c.courseId}</td>
                <td>{c.visitCount}</td>
                <td>{c.acceptedItemCount}</td>
                <td data-testid="doctor-lens-journey-completed">
                  {c.completedItemCount !== null
                    ? c.completedItemCount
                    : JOURNEY_COMPLETION_UNAVAILABLE_COPY}
                </td>
                <td data-testid="doctor-lens-journey-remaining">
                  {c.remainingPlannedCount !== null ? c.remainingPlannedCount : '-'}
                </td>
                <td>{inrWordToRs(formatPaymentAmount(c.collectedMinorUnits, 'INR'))}</td>
                <td>
                  <AcBadge tone="neutral">
                    {JOURNEY_LEDGER_STATE_LABELS[c.ledgerState] ?? c.ledgerState}
                  </AcBadge>
                  {c.outstandingMinorUnits !== null ? (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {inrWordToRs(formatPaymentAmount(c.outstandingMinorUnits, 'INR'))}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
    </SectionShell>
  );
}

// --- Commercial evidence (accepted amounts + recorded payments + voids) ----

function CommercialSection({ lens }: { lens: DoctorPatientLens }): JSX.Element {
  const tc = lens.treatmentCheckout;
  const pay = lens.payments;
  const history = lens.paymentHistory;
  return (
    <SectionShell
      eyebrow="Commercial evidence"
      title="Accepted treatment and payments"
      sub="Truthful evidence to discuss and continue treatment. No account balance is computed."
      testId="doctor-lens-commercial"
    >
      {tc.availability === 'unavailable' ? (
        <p className="text-sm text-muted-foreground">{TREATMENT_SUMMARY_UNAVAILABLE_COPY}</p>
      ) : tc.recommendedItemCount === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="doctor-lens-no-treatment">
          {TREATMENT_NO_ITEMS_COPY}
        </p>
      ) : (
        <div className="kpi-grid" data-testid="doctor-lens-treatment-kpis">
          <KpiTile
            label="Treatment"
            value={conversionStateLabel(tc.commercialConversionState)}
            testId="doctor-lens-conversion"
          />
          <KpiTile
            label="Accepted items"
            value={String(tc.acceptedItemCount)}
            sub={`${tc.declinedItemCount} declined, ${tc.deferredItemCount} deferred`}
            testId="doctor-lens-accepted-count"
          />
          <KpiTile
            label="Accepted amount"
            value={
              tc.acceptedEstimateInr !== null
                ? rupeeGlyphToRs(formatWholeInr(tc.acceptedEstimateInr))
                : 'Not configured'
            }
            tone="revenue"
            testId="doctor-lens-accepted-amount"
          />
        </div>
      )}

      {/* Recorded (non-voided) payment evidence. NO balance is computed. */}
      <div className="mt-3">
        <p className="text-sm font-medium">
          {PAYMENT_STATE_LABELS[pay.state]}{' '}
          <span className="text-xs text-muted-foreground" data-testid="doctor-lens-balance-copy">
            ({PAYMENT_BALANCE_COPY})
          </span>
        </p>
        {pay.items.length > 0 ? (
          <table className="table mt-2" data-testid="doctor-lens-payments-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {pay.items.map((p) => (
                <tr key={p.paymentId} data-testid="doctor-lens-payment-row">
                  <td>{fmtIstDate(p.occurredAt)}</td>
                  <td>{inrWordToRs(formatPaymentAmount(p.amountMinorUnits, p.currency))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      {/* Void history -- correction trail; the void reason + actor are
          supervisor-tier and ABSENT for the doctor. Never active collection. */}
      <div className="mt-3">
        <p className="text-sm font-medium">Void history</p>
        {history.state === 'unavailable' ? (
          <p className="text-sm text-muted-foreground">{PAYMENT_HISTORY_UNAVAILABLE_COPY}</p>
        ) : history.voided.length === 0 ? (
          <p className="text-sm text-muted-foreground">{PAYMENT_HISTORY_NONE_COPY}</p>
        ) : (
          <table className="table mt-2" data-testid="doctor-lens-void-table">
            <thead>
              <tr>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Voided</th>
              </tr>
            </thead>
            <tbody>
              {history.voided.map((v) => (
                <tr key={v.paymentId} data-testid="doctor-lens-void-row">
                  <td>{inrWordToRs(formatPaymentAmount(v.amountMinorUnits, v.currency))}</td>
                  <td>{paymentMethodLabel(v.method)}</td>
                  <td>
                    <AcBadge tone="danger">{PAYMENT_VOIDED_STATUS_LABEL}</AcBadge>
                  </td>
                  <td>{v.voidedAt !== null ? fmtIstDate(v.voidedAt) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </SectionShell>
  );
}

// --- Clinical case history -> the PATIENT TIMELINE (door 4) ----------------
//
// DOCTOR-OS-BUILD-PHASE-1-V1 (merged design #412 s.7.4, mockup 06 P1): the
// finalized-visit history renders as ONE CARD PER VISIT, newest first --
// labeled fields, a Finalized chip, and the one Open-visit door to the
// read-only sheet. READ-ONLY composition over the SAME fenced lens wire
// (doctor-only; clinic-fenced; the forbidden-balance canaries and masked
// contact hold byte-for-byte). The card renders ONLY fields the wire carries;
// the EVIDENCE COUNT is structurally ABSENT until the Tier-2
// clinical-evidence package lands (safe default S10: absent, never "0").

// --- The CONTINUITY SNAPSHOT (LAKSHMI-2 ruling 2026-08-16) ------------------
//
// HISTORY-EXISTS != CONTINUITY-EXISTS: a returning patient's prior context
// renders ON PAGE OPEN, zero extra navigation clicks -- the one-glance
// recall Dr. Lakshmi's finding demanded. Composed by THE ONE owner
// (lib/domain/clinical/visit-continuity.ts); the consult Continue-Care
// panel (DOCTOR-INTAKE-HISTORY-V1) renders the SAME contract.

function ContinuitySnapshot({ lens }: { lens: DoctorPatientLens }): JSX.Element | null {
  const c = lens.continuity;
  if (c === undefined || c.lastVisit === null) return null;
  const facts = c.intakeFacts;
  const ladder = deriveCountsStageLadder(
    c.treatmentCounts?.acceptedItemCount ?? null,
    c.treatmentCounts?.completedItemCount ?? null,
  );
  const nextLine =
    c.nextPlanned.kind === 'booking'
      ? `${fmtIstDateTime(c.nextPlanned.scheduledAtIso)}${c.nextPlanned.doctorName !== null ? ` - with ${c.nextPlanned.doctorName}` : ''}`
      : c.nextPlanned.kind === 'intent'
        ? nextVisitIntentLine({
            optionKey: c.nextPlanned.optionKey,
            optionLabel: c.nextPlanned.optionLabel,
            treatmentDoctorName: c.nextPlanned.treatmentDoctorName,
            closedAt: null,
          })
        : NEXT_VISIT_NONE_COPY;
  const keyFacts: FieldGridField[] = [
    {
      label: 'Last visit',
      value: (
        <span data-testid="doctor-lens-continuity-lastvisit">
          {fmtIstDate(c.lastVisit.visitDateIso)} - {c.lastVisit.summaryLine}
        </span>
      ),
    },
    ...(facts !== null
      ? [
          {
            label: 'Allergies',
            value:
              facts.allergies.length > 0 ? (
                <span data-testid="doctor-lens-continuity-allergies">
                  {facts.allergies.join(', ')}
                </span>
              ) : undefined,
            missingLabel: 'Not recorded',
          },
          {
            label: 'Medications',
            value:
              facts.medications.length > 0
                ? facts.medications.join(', ')
                : undefined,
            missingLabel: 'Not recorded',
          },
          {
            label: 'Conditions',
            value:
              facts.conditions.length > 0
                ? facts.conditions.join(', ')
                : undefined,
            missingLabel: 'Not recorded',
          },
          ...(facts.onsetLabel !== null || facts.severityLabel !== null
            ? [
                {
                  label: 'Complaint context',
                  value: [
                    facts.onsetLabel !== null ? `Onset: ${facts.onsetLabel}` : null,
                    facts.severityLabel !== null
                      ? `Severity: ${facts.severityLabel}`
                      : null,
                  ]
                    .filter((s): s is string => s !== null)
                    .join(' - '),
                },
              ]
            : []),
        ]
      : [
          {
            label: 'Previous intake',
            value: (
              <span data-testid="doctor-lens-continuity-intake-absent">
                {historyIntakeAbsentCopy(lens.historyIntakeAvailability)}
              </span>
            ),
          },
        ]),
    ...(ladder !== null
      ? [
          {
            label: 'Treatment state',
            value: (
              <span data-testid="doctor-lens-continuity-stage">
                {ladder.complete
                  ? `Course complete - ${ladder.doneCount} of ${ladder.stageTotal} done`
                  : `Stage ${ladder.stageNumber} of ${ladder.stageTotal}`}
              </span>
            ),
          },
        ]
      : []),
    { label: 'Next planned', value: nextLine },
  ];
  return (
    <div data-testid="doctor-lens-continuity">
      {facts !== null ? (
        <p className="ac-section__sub" data-testid="doctor-lens-continuity-provenance">
          Key facts below are {facts.provenanceLabel.toLowerCase()}
          {facts.capturedAt !== null ? ` (captured ${fmtIstDate(facts.capturedAt)})` : ''}
          .
        </p>
      ) : null}
      <FieldGrid fields={keyFacts} />
    </div>
  );
}

function CaseHistorySection({ lens }: { lens: DoctorPatientLens }): JSX.Element {
  const ch = lens.caseHistory;
  return (
    <SectionShell
      eyebrow="Clinical"
      title="Case history"
      sub="This patient's clinical story, newest first. The snapshot recalls the last visit at a glance; open a visit for the full case sheet."
      testId="doctor-lens-case-history"
    >
      <ContinuitySnapshot lens={lens} />
      {ch.entries.length === 0 ? (
        <EmptyState
          testId="doctor-lens-case-history-empty"
          title="No finalized clinical visits in your clinic yet."
          body="Visits become clinical history once the case sheet is finalized."
        />
      ) : (
        <ol className="doctor-timeline" data-testid="doctor-lens-timeline">
          {ch.entries.map((e) => (
            <li
              key={e.caseSheetId}
              className="doctor-timeline__card"
              data-testid="doctor-lens-timeline-card"
            >
              <div className="doctor-timeline__head">
                <span className="doctor-timeline__date">
                  {fmtIstDate(e.createdAt)}
                </span>
                <AcBadge tone="positive">Finalized</AcBadge>
                <span className="doctor-timeline__spacer" />
                <Link
                  to={`/dashboard/doctor/consult/${e.appointmentId}`}
                  className="ac-btn ac-btn--ghost ac-btn--sm"
                  data-testid="doctor-lens-case-history-open"
                >
                  Open visit
                </Link>
              </div>
              <FieldGrid
                fields={[
                  {
                    label: 'Reason',
                    value:
                      e.chiefComplaint ?? e.chiefComplaintCategory ?? undefined,
                    missingLabel: 'Not recorded',
                  },
                  {
                    label: 'Diagnosis',
                    value: e.findingsDiagnosis?.trim() || e.diagnosisCategory || undefined,
                    missingLabel: 'Not recorded',
                  },
                  {
                    label: 'Plan',
                    value:
                      e.planItemCount === 0
                        ? 'No procedures'
                        : `${e.planItemCount} item${e.planItemCount === 1 ? '' : 's'} - ${rupeeGlyphToRs(formatWholeInr(e.estimatedTotal))}`,
                  },
                ]}
              />
              {/* Tier-2 evidence badges (DOCTOR-CLINICAL-EVIDENCE-TIER2-V1):
                  render ONLY when the wire carries evidenceCounts (the B4
                  flag on). Absent wire = absent badges, byte-identical to
                  Tier 1 (safe default S10: absent, never "0"). */}
              {e.evidenceCounts !== undefined &&
              Object.keys(e.evidenceCounts).length > 0 ? (
                <div
                  className="doctor-timeline__evidence"
                  data-testid="doctor-lens-timeline-evidence"
                >
                  {Object.entries(e.evidenceCounts)
                    .filter(([, count]) => count > 0)
                    .map(([klass, count]) => (
                      <AcBadge key={klass} tone="pool">
                        {count} {evidenceClassLabel(klass)}
                        {count === 1 ? '' : 's'}
                      </AcBadge>
                    ))}
                </div>
              ) : null}
              {/* CONTINUITY EXPANSION (LAKSHMI-2 ruling, outcome 3): ONE
                  click composes the FULL recorded visit in place -- the
                  previous intake answers (read-only, provenance shown),
                  findings, notes, treatments + amounts. Compact snapshot
                  first; never permanently expanded (a native details, the
                  progressive-reveal grammar). */}
              <details
                className="patient360-secondary"
                data-testid={`doctor-lens-visit-expand-${e.appointmentId}`}
              >
                <summary className="cursor-pointer text-sm font-medium text-primary">
                  Full visit + previous intake
                </summary>
                <div className="mt-2">
                  <FieldGrid
                    fields={[
                      {
                        label: 'Findings',
                        value: e.findingsDiagnosis ?? undefined,
                        missingLabel: 'Not recorded',
                      },
                      {
                        label: 'Notes',
                        value: e.clinicalNote ?? undefined,
                        missingLabel: 'Not recorded',
                      },
                      {
                        label: 'Completed here',
                        value:
                          (e.completedProcedures ?? []).length > 0
                            ? (e.completedProcedures ?? []).join(', ')
                            : undefined,
                        missingLabel: 'None completed at this visit',
                      },
                      {
                        label: 'Payments',
                        value:
                          (e.payments ?? []).length > 0
                            ? (e.payments ?? [])
                                .map(
                                  (p) =>
                                    `${inrWordToRs(formatPaymentAmount(p.amountMinorUnits, 'INR'))} (${paymentMethodLabel(p.method)})`,
                                )
                                .join(', ')
                            : undefined,
                        missingLabel: 'None recorded against this visit',
                      },
                    ]}
                  />
                  {e.intake !== undefined && e.intake.answers.length > 0 ? (
                    <div
                      className="mt-2"
                      data-testid={`doctor-lens-visit-intake-${e.appointmentId}`}
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Previous intake (patient-reported, read-only)
                      </p>
                      {e.intake.captureStatus === 'in_progress' ? (
                        <p className="text-xs text-muted-foreground">Capture in progress · saved answers</p>
                      ) : null}
                      <FieldGrid
                        fields={e.intake.answers.map((a) => ({
                          label: a.prompt,
                          value:
                            [
                              ...a.selectedLabels,
                              ...(a.detailText !== null &&
                              a.detailText.trim() !== ''
                                ? [a.detailText]
                                : []),
                            ].join(', ') || undefined,
                          missingLabel: 'Not answered',
                        }))}
                      />
                    </div>
                  ) : (
                    <p
                      className="mt-2 text-sm text-muted-foreground"
                      data-testid={`doctor-lens-visit-intake-absent-${e.appointmentId}`}
                    >
                      {historyIntakeAbsentCopy(lens.historyIntakeAvailability)}
                    </p>
                  )}
                </div>
              </details>
            </li>
          ))}
        </ol>
      )}
      {ch.crossClinicCount > 0 ? (
        <p className="panel-caveat mt-2" data-testid="doctor-lens-cross-clinic">
          {ch.crossClinicCount} finalized visit
          {ch.crossClinicCount === 1 ? '' : 's'} at other clinics in your
          organization - content stays with that clinic (count only).
        </p>
      ) : null}
    </SectionShell>
  );
}

// --- The page body (pure; testable via react-dom/server) -------------------

export function DoctorPatientLensContent({
  lens,
  origin = null,
  originAppointmentId = null,
}: {
  lens: DoctorPatientLens;
  /** BUILD 3 (L-09): the door this record was reached through. Absent = the
   *  honest default (patient search), which is what every pre-existing caller
   *  renders -- byte-identical to the old constant. */
  readonly origin?: LensOrigin | null;
  readonly originAppointmentId?: number | null;
}): JSX.Element {
  // THE ONE return-to-origin primitive decides both the href AND the label,
  // so the two can never disagree again (the L-09 defect was exactly that).
  const backDoor = lensBackDoor(origin, originAppointmentId);
  const p = lens.patient;
  const identityFields: FieldGridField[] = [
    { label: 'Patient ID (UMR)', value: <span data-testid="doctor-lens-umr">{p.umr}</span> },
    {
      label: 'Demographics',
      value: genderAgeLabel(p.gender ?? 'unknown', p.ageYears, p.ageIsApproximate),
    },
    {
      label: 'Registered at',
      value: p.registeredClinicLabel,
      missingLabel: 'Not recorded',
    },
    // Contact stays MASKED on the doctor lens (reveal is not a doctor action).
    {
      label: 'Contact',
      value: (
        <span data-testid="doctor-lens-contact-masked">
          <AcBadge tone="neutral">Masked</AcBadge>
        </span>
      ),
    },
  ];
  return (
    <div className="doctor-portal" data-testid="doctor-lens">
      {/* V7-P4 adoption (Benchmark Book A row A4 ADOPT -- record anatomy):
          the patient identity strip renders through the shared RecordHeader
          -- audience is the REAL viewer role (this route is doctor-fenced,
          RequireDoctorLens), and the SAME already-lawful identity fields
          move into the header's facts slot verbatim (labels, masked-contact
          badge, and test ids preserved; the separate Identity section folds
          in). The wire is untouched -- the clinical fence stays SELECT-level. */}
      {/* T2 (SHIPPED-WORK-TRUTH-CORRECTIVE-V1): the lens jargon is dead as
          user-facing copy (machine words never label an operator surface);
          the record wears the plain product word. */}
      <RecordHeader
        audience="doctor"
        eyebrow="Patient"
        name={p.fullName ?? `Patient ${p.umr}`}
        facts={identityFields}
        actions={
          <Link
            to={backDoor.href}
            className="ac-btn ac-btn--ghost ac-btn--sm"
            data-testid="doctor-lens-back-to-search"
          >
            {backDoor.label}
          </Link>
        }
        testId="doctor-lens-record-header"
      />
      <p className="ac-section__sub" data-testid="doctor-lens-contact-note">
        {DOCTOR_CONTACT_MASKED_COPY}
      </p>

      <ContinueCareBand lens={lens} />

      <AppointmentLane
        eyebrow="Today"
        title="Today's visits"
        rows={lens.todayAppointments}
        emptyBody="No appointment for this patient today."
        testId="doctor-lens-today"
      />

      {/* PATIENT-360-V1 (mockup MATCH order): TREATMENTS before the clinical
          case history; the separate Follow-up section is FOLDED into the
          next-visit band above (one fact, one home -- the followUp wire
          field's truth now renders as the band's booked/intent/none line). */}
      <TreatmentJourneySection lens={lens} />

      <CommercialSection lens={lens} />

      <CaseHistorySection lens={lens} />

      <AppointmentLane
        eyebrow="Recent"
        title="Recent visits"
        rows={lens.recentVisits}
        emptyBody="No past visits for this patient in your clinic."
        testId="doctor-lens-recent"
      />

      <AppointmentLane
        eyebrow="Upcoming"
        title="Upcoming appointments"
        rows={lens.upcomingAppointments}
        emptyBody="No upcoming appointments scheduled."
        testId="doctor-lens-upcoming"
      />

      <ConsentStatusSection lens={lens} />

      <SectionShell eyebrow="External" title="EHNote coexistence" testId="doctor-lens-external">
        <p className="text-sm text-muted-foreground">{EXTERNAL_RECORD_COPY}</p>
        <p className="text-xs text-muted-foreground">{EXTERNAL_PROVENANCE_COPY}</p>
      </SectionShell>
    </div>
  );
}

export default function DoctorPatientLensPage(): JSX.Element {
  const { patientId } = useParams<{ patientId: string }>();
  // BUILD 3 (L-09): the URL is the whole carrier -- a reload, a shared link and
  // the browser Back all agree, and no storage is introduced.
  const [searchParams] = useSearchParams();
  const origin = parseLensOrigin(searchParams.get('from'));
  const originAppointmentId = Number.parseInt(
    searchParams.get('fromId') ?? '',
    10,
  );
  const [lens, setLens] = useState<DoctorPatientLens | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (patientId === undefined) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDoctorPatientLens(patientId)
      .then((data) => {
        if (!cancelled) setLens(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLens(null);
          setError(e instanceof Error ? e.message : 'Could not load the patient lens');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  if (loading) return <LoadingState label="Loading patient lens..." />;
  if (error !== null) {
    return (
      <div className="doctor-portal">
        <PageHeader title="Patient" />
        <DashboardLoadError message={error} />
        <p className="mt-3 text-sm">
          <Link to={DOCTOR_PATIENTS_HREF} className="text-primary underline-offset-4 hover:underline">
            Back to patient search
          </Link>
        </p>
      </div>
    );
  }
  if (lens === null) return <LoadingState label="Loading patient lens..." />;
  return (
    <DoctorPatientLensContent
      lens={lens}
      origin={origin}
      originAppointmentId={
        Number.isFinite(originAppointmentId) ? originAppointmentId : null
      }
    />
  );
}
